import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { connect } from "react-redux";
import NavBar from "../components/layout/NavBar";
import OuterContainer from "../components/layout/OuterContainer";
import RoundedImage from "../components/RoundedImage";
import Loader from "../components/Loading";
import unknownAvatar from "../assets/unknown_gw.png";
import { request } from "../services/networking/request";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import { User as UserIcon, Camera, Shield, Plus, X, CheckCircle, Bell, MapPin, Hash, Trash2, Edit3 } from "lucide-react";

/* ============ Styled Components (styling only) ============ */

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #fafbff 0%, #f8fafc 50%, #f1f5f9 100%);
  // padding-top: 64px;
`;

const Header = styled.div`
  text-align: center;
  margin: 40px 0 24px;

  /* Adjust margins for mobile */
  @media screen and (max-width: 768px) {
    margin: 32px 0 20px;
  }

  @media screen and (max-width: 480px) {
    margin: 24px 0 16px;
  }
`;

const Title = styled.h1`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 34px;
  font-weight: 800;
  letter-spacing: 0.1px;
  color: #0f172a;
  margin: 0;
  text-shadow: 0 1px 0 rgba(16, 24, 40, 0.04);

  svg {
    color: #3b82f6;
  }

  /* Responsive font sizes for mobile */
  @media screen and (max-width: 768px) {
    font-size: 28px;
    gap: 8px;
  }

  @media screen and (max-width: 480px) {
    font-size: 24px;
    gap: 6px;
    letter-spacing: 0px;
  }
`;

const Subtitle = styled.p`
  margin: 10px 0 28px;
  color: #64748b;
  font-size: 16px;
  line-height: 1.5;

  /* Responsive font size for mobile */
  @media screen and (max-width: 768px) {
    font-size: 15px;
    margin: 8px 0 24px;
  }

  @media screen and (max-width: 480px) {
    font-size: 14px;
    margin: 6px 0 20px;
  }
`;

const TabNav = styled.div`
  display: flex;
  gap: 2px;
  background: #f8fafc;
  border-radius: 12px;
  padding: 4px;
  box-shadow: inset 0 1px 3px rgba(15, 23, 42, 0.1);
  width: 100%;
  max-width: 480px;
  margin: 0 auto;

  @media (max-width: 640px) {
    width: 100%;
    max-width: 100%;
  }
`;

const TabButton = styled.button`
  flex: 1 1 0;
  min-width: 0;
  background: ${(props) => (props.active ? "#ffffff" : "transparent")};
  border: none;
  border-radius: 10px;
  padding: 8px 12px;
  font-weight: 700;
  font-size: 0.85rem;
  color: ${(props) => (props.active ? "#3b82f6" : "#64748b")};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${(props) =>
    props.active ? "0 2px 8px rgba(15, 23, 42, 0.1)" : "none"};
  transform: ${(props) => (props.active ? "translateY(-1px)" : "none")};
  white-space: nowrap;
  text-align: center;

  &:hover {
    color: #3b82f6;
    background: ${(props) =>
      props.active ? "#ffffff" : "rgba(255, 255, 255, 0.5)"};
  }

  @media (max-width: 640px) {
    padding: 8px 4px;
    font-size: 0.75rem;
  }

  @media (max-width: 480px) {
    font-size: 0.65rem;
    padding: 8px 2px;
    white-space: normal;
    line-height: 1.2;
  }
`;

const CardsWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  margin: 0 0 72px;
`;

const Card = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
  border-radius: 16px;
  padding: 32px;
  width: 100%;
  max-width: 760px;
  border: 1px solid #f1f5f9;
  box-shadow: 0 8px 32px rgba(15, 23, 42, 0.12);
  transition: all 0.2s ease;

  &:hover {
    border-color: #e2e8f0;
    box-shadow: 0 12px 40px rgba(15, 23, 42, 0.15);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 24px;
    border-radius: 12px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 24px;
  color: #0f172a;
  letter-spacing: -0.02em;
`;

const ProfileFlex = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  div:first-child {
    letter-spacing: 0.2px;
  }
`;

const ChangePhotoButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 8px 14px;
  background: #ffffff;
  border: 1px solid #e6edf7;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  cursor: pointer;
  transition: background 120ms ease, transform 120ms ease, box-shadow 120ms ease;

  svg {
    color: #0f172a;
    min-width: 16px;
  }

  &:hover {
    background: #f5f8ff;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(2, 8, 23, 0.08);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px 24px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  color: #475569;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  
  /* Make asterisks red for required fields */
  & span[style*="color: red"], 
  & > *[style*="color: red"] {
    color: #ef4444 !important;
  }
  
  /* Target asterisks specifically */
  &::after {
    content: "";
  }
  
  /* Style asterisks that appear after the label text */
  :global(*) {
    color: inherit;
  }
`;

const RequiredAsterisk = styled.span`
  color: #ef4444;
  margin-left: 2px;
`;

const Input = styled.input`
  padding: 12px 12px;
  border: 1px solid #e6edf7;
  border-radius: 10px;
  font-size: 14px;
  color: #0f172a;
  width: 100%;
  background: #ffffff;
  transition: box-shadow 120ms ease, border-color 120ms ease;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    border-color: #93c5fd;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
`;

const TextArea = styled.textarea`
  padding: 12px;
  border: 1px solid #e6edf7;
  border-radius: 10px;
  font-size: 14px;
  color: #0f172a;
  width: 100%;
  resize: vertical;
  min-height: 96px;
  transition: box-shadow 120ms ease, border-color 120ms ease;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    border-color: #93c5fd;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
`;

const BioSection = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
`;

const PortfolioSection = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 6px;
`;

const PortfolioRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  input {
    flex: 1;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 1px dashed #cfd8e3;
  border-radius: 10px;
  background: #f8fafc;
  color: #059669;
  cursor: pointer;
  transition: background 120ms ease, transform 120ms ease, color 120ms ease;

  &:hover {
    background: #eef4ff;
    border-color: #059669;
    transform: translateY(-1px);
  }
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 1px solid #fecaca;
  border-radius: 10px;
  background: #fef2f2;
  color: #dc2626;
  cursor: pointer;
  transition: background 120ms ease, transform 120ms ease, color 120ms ease;

  &:hover {
    background: #fee2e2;
    border-color: #dc2626;
    transform: translateY(-1px);
  }
`;

const SaveButton = styled.button`
  margin-top: 26px;
  padding: 12px 22px;
  background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  float: right;
  box-shadow: 0 14px 28px rgba(37, 99, 235, 0.18);
  transition: transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 18px 34px rgba(37, 99, 235, 0.22);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(248, 250, 252, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
`;

/* ===== Security / Password UI ===== */

const PwRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
`;

const PwField = styled.div`
  display: grid;
  gap: 6px;
  position: relative;
`;

const ToggleBtn = styled.button`
  position: absolute;
  right: 8px;
  top: 34px;
  transform: translateY(-50%);
  border: 1px solid #e6edf7;
  background: #fff;
  border-radius: 8px;
  font-size: 12px;
  padding: 6px 8px;
  color: #475569;
  cursor: pointer;

  &:hover {
    background: #f5f8ff;
  }
`;

const SmallNote = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const EmailVerifiedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  
  svg {
    color: #10b981;
    min-width: 16px;
    min-height: 16px;
  }
`;

const EmailInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-wrap: wrap;
`;

const ErrorText = styled.div`
  color: #dc2626;
  font-size: 12px;
  font-weight: 700;
`;

const SuccessText = styled.div`
  color: #16a34a;
  font-size: 13px;
  font-weight: 800;
`;

const BarWrap = styled.div`
  height: 8px;
  border-radius: 999px;
  background: #eef2f7;
  overflow: hidden;
`;

const Bar = styled.div`
  height: 100%;
  width: ${({ pct }) => pct}%;
  background: ${({ color }) => color};
  transition: width 160ms ease;
  border-radius: 999px;
`;

const Skeleton = styled.div`
  border-radius: 12px;
  background: linear-gradient(90deg, #f1f5f9, #f8fafc, #f1f5f9);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite linear;
  height: ${(p) => p.h || "14px"};
  width: ${(p) => p.w || "100%"};

  @keyframes shimmer {
    from {
      background-position: 200% 0;
    }
    to {
      background-position: -200% 0;
    }
  }
`;

/* ===== Preferences UI ===== */

const PreferencesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PreferenceCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  position: relative;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #cbd5e1;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
  }
`;

const PreferenceHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const PreferenceName = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PreferenceActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${props => props.danger ? '#fee2e2' : '#e2e8f0'};
  border-radius: 8px;
  background: ${props => props.danger ? '#fef2f2' : '#ffffff'};
  color: ${props => props.danger ? '#dc2626' : '#475569'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.danger ? '#fee2e2' : '#f8fafc'};
    border-color: ${props => props.danger ? '#dc2626' : '#94a3b8'};
  }
`;

const PreferenceDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 13px;
  color: #64748b;
`;

const PreferenceTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f1f5f9;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
`;

const TagGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 9999;
  padding: 80px 20px 20px 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalContent = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
    
    &:hover {
      background: #94a3b8;
    }
  }
  
  /* For Firefox */
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const Select = styled.select`
  width: 100%;
  max-width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  font-size: 14px;
  background: white;
  color: #374151;
  transition: all 0.2s ease;
  box-sizing: border-box;
  min-width: 0;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
  }

  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;
  padding-right: 40px;
`;

const CheckboxGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: #f8fafc;
  }
  
  input[type="checkbox"] {
    margin: 0;
  }
`;

const TagInputContainer = styled.div`
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 8px 12px;
  min-height: 44px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  cursor: text;
  background: white;
  transition: all 0.2s ease;

  &:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
  }
`;

const KeywordTag = styled.div`
  display: flex;
  align-items: center;
  background: #3b82f6;
  color: white;
  padding: 4px 8px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
  gap: 4px;
  
  button {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 0;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

const TagInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  flex: 1;
  min-width: 120px;
  font-size: 14px;
  color: #374151;
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
  flex-shrink: 0;
  background: white;
`;

const ModalButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.primary ? `
    background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
    color: white;
    border: none;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    }
  ` : `
    background: white;
    color: #475569;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f9fafb;
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #64748b;
  
  svg {
    margin-bottom: 16px;
    color: #94a3b8;
  }
  
  h3 {
    margin: 0 0 8px;
    font-size: 16px;
    font-weight: 600;
    color: #475569;
  }
  
  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
  }
`;


/* ============ Component (logic + new Security tab) ============ */

const AccountSettings = ({ state }) => {
  const [activeTab, setActiveTab] = useState("profile");
  const [user, setUser] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInput = useRef(null);

  // Password form state
  const [pw, setPw] = useState({
    current: "",
    next: "",
    confirm: "",
  });
  const [pwVisible, setPwVisible] = useState({
    current: false,
    next: false,
    confirm: false,
  });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");

  // Notification preferences state
  const [notificationPreferences, setNotificationPreferences] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showPreferenceModal, setShowPreferenceModal] = useState(false);
  const [editingPreference, setEditingPreference] = useState(null);
  const [savingPreference, setSavingPreference] = useState(false);
  const [preferenceForm, setPreferenceForm] = useState({
    name: "",
    description: "",
    task_type: "",
    min_budget: "",
    max_budget: "",
    location_type: "any",
    zipcode: "",
    radius: 25,
    keywords: [],
    categories: [],
    max_notifications_per_day: 10
  });

  // Delete state
  const [deletingPreference, setDeletingPreference] = useState(null);

  useEffect(() => {
    setLoading(true);
    request("POST", API_CONSTANT_MAP.get_account_info)
      .then((res) => {
        const d = res.data.data;
        setUser(d);
        setForm({
          first_name: d.first_name || "",
          last_name: d.last_name || "",
          phone_number: d.phone_number || "",
          zipcode: d.zipcode || "",
          website: d.website || "",
          bio: d.bio || "",
          portfolio: Array.isArray(d.portfolio_links)
            ? d.portfolio_links
            : [""],
          avatar: d.avatar || "",
          email_verified: d.email_verified !== undefined ? d.email_verified : true,
        });
      })
      .finally(() => setLoading(false));
    
    // Load notification preferences on component mount for tab count
    loadNotificationPreferences();
  }, []);

  // Check URL parameter for tab on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam === 'preferences' || tabParam === 'notifications') {
      setActiveTab('preferences');
    } else if (tabParam === 'security') {
      setActiveTab('security');
    }
  }, []);

  // Load categories and preferences when preferences tab becomes active
  useEffect(() => {
    if (activeTab === "preferences") {
      loadCategories();
      loadNotificationPreferences();
    }
  }, [activeTab]);

  const onChange = (e) => {
    const { id, value } = e.target;
    setForm((f) => ({ ...f, [id]: value }));
  };

  const onPortfolioChange = (i, val) => {
    setForm((f) => {
      const p = [...f.portfolio];
      p[i] = val;
      return { ...f, portfolio: p };
    });
  };

  const addPortfolio = () => {
    setForm((f) => ({ ...f, portfolio: [...f.portfolio, ""] }));
  };

  const removePortfolio = (index) => {
    setForm((f) => ({
      ...f,
      portfolio: f.portfolio.filter((_, i) => i !== index),
    }));
  };

  const onAvatarClick = () => fileInput.current.click();

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const preview = URL.createObjectURL(file);
    setForm((f) => ({ ...f, avatar: preview }));

    const initUrl = API_CONSTANT_MAP.get_presigned_upload_url;
    if (!initUrl || typeof initUrl !== "string") {
      console.error("upload_avatar_init is not configured:", initUrl);
      alert("Avatar upload endpoint is not configured.");
      return;
    }

    let initResp;
    try {
      initResp = await request("POST", initUrl, {
        filename: file.name,
        content_type: file.type,
      });
    } catch (err) {
      console.error("Upload init failed:", err);
      alert(err?.error?.message || "Failed to start avatar upload.");
      return;
    }

    const presignedUrl = initResp?.data?.data?.presigned_url;
    const publicUrl = initResp?.data?.data?.image_url;

    if (!presignedUrl || !publicUrl) {
      console.error("Bad init response shape:", initResp?.data);
      alert("Server did not return an upload URL.");
      return;
    }

    try {
      const put = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!put.ok) {
        throw new Error(`S3 upload failed (${put.status})`);
      }
    } catch (err) {
      console.error("S3 PUT failed:", err);
      alert(err.message || "S3 upload failed.");
      return;
    }

    setForm((f) => ({ ...f, avatar: publicUrl }));
  };

  const saveProfile = async () => {
    setLoading(true);
    try {
      await request("POST", API_CONSTANT_MAP.update_account_details_v2, {
        first_name: form.first_name,
        last_name: form.last_name,
        phone_number: form.phone_number,
        zipcode: form.zipcode,
        website: form.website,
        bio: form.bio,
        portfolio_links: form.portfolio,
        avatar: form.avatar,
      });
      console.log("Profile saved");
    } catch {
      console.log("Failed to save");
    } finally {
      setLoading(false);
    }
  };

  // ===== Password helpers =====
  const scorePassword = (s) => {
    if (!s) return 0;
    let score = 0;
    if (s.length >= 8) score += 1;
    if (/[A-Z]/.test(s)) score += 1;
    if (/[a-z]/.test(s)) score += 1;
    if (/\d/.test(s)) score += 1;
    if (/[^A-Za-z0-9]/.test(s)) score += 1;
    return Math.min(score, 5);
  };
  const strength = scorePassword(pw.next);
  const pct = [0, 25, 45, 65, 85, 100][strength];
  const color =
    strength <= 1
      ? "#ef4444"
      : strength === 2
      ? "#f59e0b"
      : strength === 3
      ? "#22c55e"
      : "#16a34a";

  const canSubmitPw =
    pw.current.length > 0 &&
    pw.next.length >= 8 &&
    pw.next === pw.confirm &&
    strength >= 2;

  const submitPassword = async () => {
    setPwError("");
    setPwSuccess("");
    if (!canSubmitPw) {
      setPwError(
        "Please fix the errors above before saving your new password."
      );
      return;
    }
    setPwSaving(true);
    try {
      // Endpoint fallback: prefer update_password, otherwise change_password
      const endpoint =
        API_CONSTANT_MAP.update_password || API_CONSTANT_MAP.change_password;

      if (!endpoint) {
        throw new Error(
          "Password endpoint not configured (update_password / change_password)."
        );
      }

      const { data } = await request("POST", endpoint, {
        current_password: pw.current,
        new_password: pw.next,
      });

      if (!data?.success) {
        const msg =
          data?.error?.message ||
          "Could not change password. Please try again.";
        throw new Error(msg);
      }

      setPwSuccess("✅ Password updated successfully.");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      setPwError(err.message || "Failed to change password.");
    } finally {
      setPwSaving(false);
    }
  };

  // ===== Notification Preferences helpers =====
  const loadCategories = async () => {
    try {
      const response = await request("POST", API_CONSTANT_MAP.fetch_categories);
      if (response?.data?.success) {
        setCategories(response.data.categories || []);
      }
    } catch (error) {
      console.error("Failed to load categories:", error);
    }
  };

  const loadNotificationPreferences = async () => {
    try {
      const response = await request("GET", API_CONSTANT_MAP.fetch_notification_preferences);
      if (response?.data?.success) {
        setNotificationPreferences(response.data.data || []);
      } else {
        setNotificationPreferences([]);
      }
    } catch (error) {
      console.error("Failed to load notification preferences:", error);
      setNotificationPreferences([]);
    }
  };

  const resetPreferenceForm = () => {
    setPreferenceForm({
      name: "",
      description: "",
      task_type: "",
      min_budget: "",
      max_budget: "",
      location_type: "any",
      zipcode: "",
      radius: 25,
      keywords: [],
      categories: [],
      max_notifications_per_day: 10
    });
  };

  const openPreferenceModal = (preference = null) => {
    // Ensure categories are loaded before opening modal
    if (categories.length === 0) {
      loadCategories();
    }
    
    if (preference) {
      setEditingPreference(preference);
      setPreferenceForm({
        name: preference.name || "",
        description: preference.description || "",
        task_type: preference.task_type || "",
        min_budget: preference.min_budget || "",
        max_budget: preference.max_budget || "",
        location_type: preference.location_type || "any",
        zipcode: preference.zipcode || "",
        radius: preference.radius || 25,
        keywords: Array.isArray(preference.keywords) ? preference.keywords : [],
        categories: preference.categories || [],
        max_notifications_per_day: preference.max_notifications_per_day || 10
      });
    } else {
      setEditingPreference(null);
      resetPreferenceForm();
    }
    setShowPreferenceModal(true);
  };

  const closePreferenceModal = () => {
    setShowPreferenceModal(false);
    setEditingPreference(null);
    resetPreferenceForm();
  };

  const handlePreferenceFormChange = (field, value) => {
    setPreferenceForm(prev => {
      let updates = { [field]: value };
      
      // Handle location type changes based on task type
      if (field === "task_type") {
        if (value === "online") {
          // If task type is remote, set location to remote and clear zipcode
          updates.location_type = "remote";
          updates.zipcode = "";
          updates.radius = 25;
        } else if (value === "in-person") {
          // If task type is in-person and location was remote, change to "any"
          if (prev.location_type === "remote") {
            updates.location_type = "any";
          }
        }
        // For "All tasks", keep current location_type as is
      }
      
      return {
        ...prev,
        ...updates
      };
    });
  };

  // Get available location options based on task type
  const getLocationOptions = (taskType) => {
    if (taskType === "online") {
      return [
        { value: "remote", label: "Remote" }
      ];
    } else if (taskType === "in-person") {
      return [
        { value: "any", label: "All" },
        { value: "zipcode", label: "Specific ZIP code area" }
      ];
    } else {
      // taskType === "" (All tasks) or any other value
      return [
        { value: "any", label: "All" },
        { value: "zipcode", label: "Specific ZIP code area" },
        { value: "remote", label: "Remote" }
      ];
    }
  };

  const toggleCategory = (categoryId) => {
    setPreferenceForm(prev => ({
      ...prev,
      categories: prev.categories.includes(categoryId)
        ? prev.categories.filter(id => id !== categoryId)
        : [...prev.categories, categoryId]
    }));
  };

  const addKeyword = (keyword) => {
    if (!keyword || typeof keyword !== 'string') return;
    const trimmedKeyword = keyword.trim();
    if (trimmedKeyword.length === 0) return;
    
    setPreferenceForm(prev => ({
      ...prev,
      keywords: prev.keywords.includes(trimmedKeyword) 
        ? prev.keywords 
        : [...prev.keywords, trimmedKeyword]
    }));
  };

  const removeKeyword = (keywordToRemove) => {
    setPreferenceForm(prev => ({
      ...prev,
      keywords: prev.keywords.filter(keyword => keyword !== keywordToRemove)
    }));
  };

  const savePreference = async () => {
    setSavingPreference(true);
    try {
      const keywords = Array.isArray(preferenceForm.keywords) 
        ? preferenceForm.keywords.filter(k => k.trim().length > 0)
        : [];

      const preferenceData = {
        ...preferenceForm,
        keywords,
        min_budget: preferenceForm.min_budget ? parseFloat(preferenceForm.min_budget) : null,
        max_budget: preferenceForm.max_budget ? parseFloat(preferenceForm.max_budget) : null,
        radius: parseInt(preferenceForm.radius)
      };

      let response;
      if (editingPreference) {
        // Update existing preference
        const endpoint = `${API_CONSTANT_MAP.update_notification_preference}/${editingPreference.id}`;
        response = await request("PUT", endpoint, preferenceData);
      } else {
        // Create new preference
        response = await request("POST", API_CONSTANT_MAP.create_notification_preference, preferenceData);
      }

      if (response?.data?.success) {
        closePreferenceModal();
        loadNotificationPreferences(); // Reload to show changes
      } else {
        console.error("Failed to save preference:", response?.data?.message || "Unknown error");
        alert(`Failed to ${editingPreference ? 'update' : 'create'} notification preference. Please try again.`);
      }
    } catch (error) {
      console.error("Failed to save preference:", error);
      alert(`Failed to ${editingPreference ? 'update' : 'create'} notification preference. Please try again.`);
    } finally {
      setSavingPreference(false);
    }
  };

  const deletePreference = async (preferenceId) => {
    setDeletingPreference(preferenceId);

    try {
      const endpoint = `${API_CONSTANT_MAP.delete_notification_preference}/${preferenceId}`;
      const response = await request("DELETE", endpoint);
      
      if (response?.data?.success) {
        loadNotificationPreferences(); // Reload to show changes
      } else {
        console.error("Failed to delete preference:", response?.data?.message || "Unknown error");
        alert("Failed to delete notification preference. Please try again.");
      }
    } catch (error) {
      console.error("Failed to delete preference:", error);
      alert("Failed to delete notification preference. Please try again.");
    } finally {
      setDeletingPreference(null);
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : `Category ${categoryId}`;
  };

  if (!form) {
    return (
      <>
        <NavBar user={state.user} />
        <PageWrapper>
          <OuterContainer>
            <Header>
              <Title>Account Settings</Title>
              <Subtitle>Manage your profile and account preferences</Subtitle>
              <TabNav>
                <TabButton style={{ pointerEvents: "none" }}>
                  <Skeleton h="16px" w="60px" />
                </TabButton>
                <TabButton style={{ pointerEvents: "none" }}>
                  <Skeleton h="16px" w="70px" />
                </TabButton>
                <TabButton style={{ pointerEvents: "none" }}>
                  <Skeleton h="16px" w="90px" />
                </TabButton>
              </TabNav>
            </Header>

            <CardsWrapper>
              {/* Profile Picture Card Skeleton */}
              <Card>
                <CardHeader>
                  <Skeleton h="22px" w="22px" />
                  <Skeleton h="22px" w="120px" />
                </CardHeader>
                <ProfileFlex>
                  <Skeleton h="84px" w="84px" style={{ borderRadius: "50%" }} />
                  <ProfileInfo>
                    <Skeleton
                      h="20px"
                      w="150px"
                      style={{ marginBottom: "8px" }}
                    />
                    <Skeleton
                      h="16px"
                      w="180px"
                      style={{ marginBottom: "12px" }}
                    />
                    <Skeleton
                      h="36px"
                      w="120px"
                      style={{ borderRadius: "8px" }}
                    />
                  </ProfileInfo>
                </ProfileFlex>
              </Card>

              {/* Basic Information Card Skeleton */}
              <Card>
                <CardHeader>
                  <Skeleton h="22px" w="22px" />
                  <Skeleton h="22px" w="140px" />
                </CardHeader>
                <Grid>
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <FieldWrapper key={i}>
                      <Skeleton
                        h="16px"
                        w="80px"
                        style={{ marginBottom: "8px" }}
                      />
                      <Skeleton h="44px" w="100%" />
                    </FieldWrapper>
                  ))}
                  <BioSection>
                    <Skeleton
                      h="16px"
                      w="60px"
                      style={{ marginBottom: "8px" }}
                    />
                    <Skeleton h="100px" w="100%" />
                  </BioSection>
                  <PortfolioSection>
                    <Skeleton
                      h="16px"
                      w="140px"
                      style={{ marginBottom: "12px" }}
                    />
                    <PortfolioRow>
                      <Skeleton h="44px" w="100%" />
                      <Skeleton h="44px" w="42px" />
                    </PortfolioRow>
                    <PortfolioRow>
                      <Skeleton h="44px" w="100%" />
                      <Skeleton h="44px" w="42px" />
                    </PortfolioRow>
                  </PortfolioSection>
                </Grid>
                <Skeleton
                  h="44px"
                  w="120px"
                  style={{ marginTop: "26px", borderRadius: "8px" }}
                />
              </Card>
            </CardsWrapper>
          </OuterContainer>
        </PageWrapper>
      </>
    );
  }

  return (
    <>
      <NavBar user={state.user} />
      <PageWrapper>
        <OuterContainer>
          <Header>
            <Title>Account Settings</Title>
            <Subtitle>Manage your profile and account preferences</Subtitle>
            <TabNav>
              <TabButton
                active={activeTab === "profile"}
                onClick={() => setActiveTab("profile")}
              >
                Profile
              </TabButton>
              <TabButton
                active={activeTab === "security"}
                onClick={() => setActiveTab("security")}
              >
                Security
              </TabButton>
              <TabButton
                active={activeTab === "preferences"}
                onClick={() => setActiveTab("preferences")}
              >
                Notifications {notificationPreferences.length > 0 && `(${notificationPreferences.length})`}
              </TabButton>
            </TabNav>
          </Header>

          {activeTab === "profile" && (
            <CardsWrapper>
              {loading && (
                <Overlay>
                  <Loader animating size={48} />
                </Overlay>
              )}

              <Card>
                <CardHeader>
                  <Camera size={22} /> Profile Picture
                </CardHeader>
                <ProfileFlex>
                  <RoundedImage
                    image={form.avatar || unknownAvatar}
                    imageWidth={84}
                    imageHeight={84}
                    borderRadiusPct={50}
                  />
                  <ProfileInfo>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 800,
                        color: "#0f172a",
                      }}
                    >
                      <span style={{ textTransform: "capitalize" }}>
                        {form.first_name} {form.last_name}
                      </span>
                    </div>
                    <EmailInfo style={{ fontSize: 14, color: "#64748b" }}>
                      <span>{user.email}</span>
                      {form.email_verified && (
                        <EmailVerifiedBadge title="Email Verified">
                          <CheckCircle size={16} />
                        </EmailVerifiedBadge>
                      )}
                    </EmailInfo>
                    <ChangePhotoButton onClick={onAvatarClick}>
                      <Camera size={16} /> Change Photo
                    </ChangePhotoButton>
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInput}
                      onChange={onFileSelected}
                      style={{ display: "none" }}
                    />
                  </ProfileInfo>
                </ProfileFlex>
              </Card>

              <Card>
                <CardHeader>
                  <UserIcon size={22} /> Basic Information
                </CardHeader>
                <Grid>
                  <FieldWrapper>
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      value={form.first_name}
                      onChange={onChange}
                      placeholder="Your first name"
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      value={form.last_name}
                      onChange={onChange}
                      placeholder="Your last name"
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <Label htmlFor="phone_number">Phone Number</Label>
                    <Input
                      id="phone_number"
                      value={form.phone_number}
                      onChange={onChange}
                      placeholder="+1 (555) 123-4567"
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <Label htmlFor="zipcode">ZIP Code</Label>
                    <Input
                      id="zipcode"
                      value={form.zipcode}
                      onChange={onChange}
                      placeholder="e.g., 90210"
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      value={form.website}
                      onChange={onChange}
                      placeholder="https://yourwebsite.com"
                    />
                  </FieldWrapper>

                  <BioSection>
                    <Label htmlFor="bio">Bio</Label>
                    <TextArea
                      id="bio"
                      rows={3}
                      value={form.bio}
                      onChange={onChange}
                      placeholder="Tell us about yourself..."
                    />
                  </BioSection>

                  <PortfolioSection>
                    <Label>Portfolio Links</Label>
                    {(form.portfolio.length > 0 ? form.portfolio : [""]).map(
                      (url, i) => (
                        <PortfolioRow key={i}>
                          <Input
                            value={url}
                            onChange={(e) =>
                              onPortfolioChange(i, e.target.value)
                            }
                            placeholder="https://yourportfolio.com"
                          />
                          {form.portfolio.length > 1 && (
                            <RemoveButton
                              onClick={() => removePortfolio(i)}
                              title="Remove this portfolio link"
                            >
                              <X size={16} />
                            </RemoveButton>
                          )}
                          {i === form.portfolio.length - 1 && (
                            <AddButton
                              onClick={addPortfolio}
                              title="Add another portfolio link"
                            >
                              <Plus size={16} />
                            </AddButton>
                          )}
                        </PortfolioRow>
                      )
                    )}
                  </PortfolioSection>
                </Grid>
                <SaveButton onClick={saveProfile}>Save Profile</SaveButton>
              </Card>
            </CardsWrapper>
          )}

          {activeTab === "security" && (
            <CardsWrapper>
              <Card>
                <CardHeader>
                  <Shield size={22} /> Change Password
                </CardHeader>

                <PwRow>
                  <PwField>
                    <Label htmlFor="current">Current Password</Label>
                    <Input
                      id="current"
                      type={pwVisible.current ? "text" : "password"}
                      value={pw.current}
                      onChange={(e) =>
                        setPw((p) => ({ ...p, current: e.target.value }))
                      }
                      placeholder="Enter current password"
                      autoComplete="current-password"
                    />
                    <ToggleBtn
                      type="button"
                      onClick={() =>
                        setPwVisible((v) => ({ ...v, current: !v.current }))
                      }
                    >
                      {pwVisible.current ? "Hide" : "Show"}
                    </ToggleBtn>
                  </PwField>

                  <PwField>
                    <Label htmlFor="next">New Password</Label>
                    <Input
                      id="next"
                      type={pwVisible.next ? "text" : "password"}
                      value={pw.next}
                      onChange={(e) => {
                        setPw((p) => ({ ...p, next: e.target.value }));
                        setPwError("");
                        setPwSuccess("");
                      }}
                      placeholder="At least 8 characters"
                      autoComplete="new-password"
                    />
                    <ToggleBtn
                      type="button"
                      onClick={() =>
                        setPwVisible((v) => ({ ...v, next: !v.next }))
                      }
                    >
                      {pwVisible.next ? "Hide" : "Show"}
                    </ToggleBtn>
                    <SmallNote>
                      Use a mix of upper & lower case, numbers, and symbols.
                    </SmallNote>
                    <BarWrap>
                      <Bar pct={pct} color={color} />
                    </BarWrap>
                  </PwField>

                  <PwField>
                    <Label htmlFor="confirm">Confirm New Password</Label>
                    <Input
                      id="confirm"
                      type={pwVisible.confirm ? "text" : "password"}
                      value={pw.confirm}
                      onChange={(e) =>
                        setPw((p) => ({ ...p, confirm: e.target.value }))
                      }
                      placeholder="Re-enter new password"
                      autoComplete="new-password"
                    />
                    <ToggleBtn
                      type="button"
                      onClick={() =>
                        setPwVisible((v) => ({ ...v, confirm: !v.confirm }))
                      }
                    >
                      {pwVisible.confirm ? "Hide" : "Show"}
                    </ToggleBtn>
                    {pw.confirm && pw.next !== pw.confirm && (
                      <ErrorText>New passwords do not match.</ErrorText>
                    )}
                  </PwField>

                  {pwError && <ErrorText>{pwError}</ErrorText>}
                  {pwSuccess && <SuccessText>{pwSuccess}</SuccessText>}

                  <SaveButton
                    style={{ float: "none", justifySelf: "end" }}
                    onClick={submitPassword}
                    disabled={!canSubmitPw || pwSaving}
                  >
                    {pwSaving ? "Saving…" : "Save New Password"}
                  </SaveButton>
                </PwRow>
              </Card>
            </CardsWrapper>
          )}

          {activeTab === "preferences" && (
            <CardsWrapper>
              <Card>
                <CardHeader>
                  <Bell size={22} /> Notification Preferences
                </CardHeader>
                
                {notificationPreferences.length === 0 ? (
                  <EmptyState>
                    <Bell size={48} />
                    <h3>No Notification Preferences</h3>
                    <p>Create your first notification rule to get alerted when tasks matching your criteria are posted.</p>
                  </EmptyState>
                ) : (
                  <PreferencesGrid>
                    {notificationPreferences.map((preference) => (
                      <PreferenceCard key={preference.id}>
                        <PreferenceHeader>
                          <PreferenceName>
                            <Bell size={16} />
                            {preference.name}
                          </PreferenceName>
                          <PreferenceActions>
                            <ActionButton
                              onClick={() => openPreferenceModal(preference)}
                              title="Edit preference"
                            >
                              <Edit3 size={14} />
                            </ActionButton>
                            <ActionButton
                              danger
                              onClick={() => deletePreference(preference.id)}
                              disabled={deletingPreference === preference.id}
                              title={deletingPreference === preference.id ? "Deleting..." : "Delete preference"}
                            >
                              {deletingPreference === preference.id ? "Deleting..." : <Trash2 size={14} />}
                            </ActionButton>
                          </PreferenceActions>
                        </PreferenceHeader>
                        
                        <PreferenceDetails>
                          {preference.description && (
                            <div style={{ marginBottom: "12px", color: "#374151" }}>
                              {preference.description}
                            </div>
                          )}
                          
                          <TagGroup>
                            {preference.task_type && (
                              <PreferenceTag>
                                {preference.task_type === "in-person" ? "📍 In-person" : "💻 Online"}
                              </PreferenceTag>
                            )}
                            
                            {preference.location_type === "zipcode" && (
                              <PreferenceTag>
                                <MapPin size={12} />
                                {preference.zipcode} ({preference.radius}mi)
                              </PreferenceTag>
                            )}
                            
                            {preference.location_type === "remote" && (
                              <PreferenceTag>
                                🌐 Remote Only
                              </PreferenceTag>
                            )}
                            
                            {(preference.min_budget || preference.max_budget) && (
                              <PreferenceTag>
                                💰 ${preference.min_budget || 0} - ${preference.max_budget || "∞"}
                              </PreferenceTag>
                            )}
                            
                            {preference.keywords && preference.keywords.length > 0 && (
                              <PreferenceTag>
                                <Hash size={12} />
                                {preference.keywords.join(", ")}
                              </PreferenceTag>
                            )}
                            
                            {preference.categories && preference.categories.length > 0 && (
                              preference.categories.map(categoryId => (
                                <PreferenceTag key={categoryId}>
                                  🏷️ {getCategoryName(categoryId)}
                                </PreferenceTag>
                              ))
                            )}
                          </TagGroup>
                          
                          <div style={{ marginTop: "8px", fontSize: "12px" }}>
                            📧 Max {preference.max_notifications_per_day} notifications/day
                            {!preference.is_active && " • Inactive"}
                          </div>
                        </PreferenceDetails>
                      </PreferenceCard>
                    ))}
                  </PreferencesGrid>
                )}
                
                <SaveButton onClick={() => openPreferenceModal()}>
                  <Plus size={16} style={{ marginRight: "8px" }} />
                  Add New Preference
                </SaveButton>
              </Card>
            </CardsWrapper>
          )}
          
          {/* Preference Modal */}
          {showPreferenceModal && (
            <ModalOverlay onClick={closePreferenceModal}>
              <Modal onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                  <ModalTitle>
                    {editingPreference ? "Edit" : "Create"} Notification Preference
                  </ModalTitle>
                  <ActionButton onClick={closePreferenceModal}>
                    <X size={16} />
                  </ActionButton>
                </ModalHeader>
                
                <ModalContent>
                  <FormRow>
                  <Label>Preference Name <RequiredAsterisk>*</RequiredAsterisk></Label>
                  <Input
                    value={preferenceForm.name}
                    onChange={(e) => handlePreferenceFormChange("name", e.target.value)}
                    placeholder="e.g., Urgent Local Tasks"
                  />
                </FormRow>
                
                <FormRow>
                  <Label>Description</Label>
                  <TextArea
                    value={preferenceForm.description}
                    onChange={(e) => handlePreferenceFormChange("description", e.target.value)}
                    placeholder="Optional description of what you want to be notified about"
                    rows={2}
                  />
                </FormRow>
                
                <FormRow>
                  <Label>Task Type</Label>
                  <Select
                    value={preferenceForm.task_type}
                    onChange={(e) => handlePreferenceFormChange("task_type", e.target.value)}
                  >
                    <option value="">All tasks</option>
                    <option value="online">Remote</option>
                    <option value="in-person">In-person</option>
                  </Select>
                </FormRow>
                
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  <FormRow>
                    <Label>Min Budget ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={preferenceForm.min_budget}
                      onChange={(e) => handlePreferenceFormChange("min_budget", e.target.value)}
                      placeholder="0"
                    />
                  </FormRow>
                  
                  <FormRow>
                    <Label>Max Budget ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={preferenceForm.max_budget}
                      onChange={(e) => handlePreferenceFormChange("max_budget", e.target.value)}
                      placeholder="No limit"
                    />
                  </FormRow>
                </div>
                
                <FormRow>
                  <Label>Location</Label>
                  <Select
                    value={preferenceForm.location_type}
                    onChange={(e) => handlePreferenceFormChange("location_type", e.target.value)}
                  >
                    {getLocationOptions(preferenceForm.task_type).map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormRow>
                
                {preferenceForm.location_type === "zipcode" && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                    <FormRow>
                      <Label>ZIP Code <RequiredAsterisk>*</RequiredAsterisk></Label>
                      <Input
                        value={preferenceForm.zipcode}
                        onChange={(e) => handlePreferenceFormChange("zipcode", e.target.value)}
                        placeholder="90210"
                      />
                    </FormRow>
                    
                    <FormRow>
                      <Label>Radius</Label>
                      <Select
                        value={preferenceForm.radius}
                        onChange={(e) => handlePreferenceFormChange("radius", e.target.value)}
                      >
                        <option value="5">5 miles</option>
                        <option value="10">10 miles</option>
                        <option value="25">25 miles</option>
                        <option value="50">50 miles</option>
                        <option value="100">100 miles</option>
                      </Select>
                    </FormRow>
                  </div>
                )}
                
                <FormRow>
                  <Label>Keywords</Label>
                  <TagInputContainer 
                    onClick={(e) => {
                      // Focus the input when clicking anywhere in the container
                      const input = e.currentTarget.querySelector('input');
                      if (input) input.focus();
                    }}
                  >
                    {preferenceForm.keywords.map((keyword, index) => (
                      <KeywordTag key={index}>
                        {keyword}
                        <button 
                          type="button"
                          onClick={() => removeKeyword(keyword)}
                          aria-label={`Remove ${keyword}`}
                        >
                          ×
                        </button>
                      </KeywordTag>
                    ))}
                    <TagInput
                      placeholder={preferenceForm.keywords.length === 0 ? "Type keywords and press Enter..." : "Add more..."}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ',') {
                          e.preventDefault();
                          const value = e.target.value.trim();
                          if (value) {
                            addKeyword(value);
                            e.target.value = '';
                          }
                        }
                        if (e.key === 'Backspace' && e.target.value === '' && preferenceForm.keywords.length > 0) {
                          removeKeyword(preferenceForm.keywords[preferenceForm.keywords.length - 1]);
                        }
                      }}
                    />
                  </TagInputContainer>
                </FormRow>
                
                <FormRow>
                  <Label>Categories</Label>
                  <CheckboxGroup>
                    {categories.length > 0 ? (
                      categories.map((category) => (
                        <CheckboxLabel key={category.id}>
                          <input
                            type="checkbox"
                            checked={preferenceForm.categories.includes(category.id)}
                            onChange={() => toggleCategory(category.id)}
                          />
                          {category.name}
                        </CheckboxLabel>
                      ))
                    ) : (
                      <div style={{ padding: "12px", color: "#64748b", fontStyle: "italic" }}>
                        Loading categories...
                      </div>
                    )}
                  </CheckboxGroup>
                </FormRow>
                
                <FormRow>
                  <Label>Max notifications/day</Label>
                  <Input
                    type="number"
                    min="1"
                    max="50"
                    value={preferenceForm.max_notifications_per_day}
                    onChange={(e) => handlePreferenceFormChange("max_notifications_per_day", e.target.value)}
                  />
                </FormRow>
                
                </ModalContent>
                
                <ModalFooter>
                  <ButtonGroup>
                    <ModalButton 
                      onClick={closePreferenceModal}
                      disabled={savingPreference}
                    >
                      Cancel
                    </ModalButton>
                    <ModalButton 
                      primary 
                      onClick={savePreference}
                      disabled={!preferenceForm.name.trim() || savingPreference}
                    >
                      {savingPreference 
                        ? "Saving..." 
                        : `${editingPreference ? "Update" : "Create"} Preference`
                      }
                    </ModalButton>
                  </ButtonGroup>
                </ModalFooter>
              </Modal>
            </ModalOverlay>
          )}

        </OuterContainer>
      </PageWrapper>
    </>
  );
};

export default connect((state) => ({ state }))(AccountSettings);
